package fr.manyverse.app.domain.repository

import fr.manyverse.app.domain.image.ImageTarget
import fr.manyverse.app.domain.model.ImageData

/**
 * Repository interface for image storage operations.
 * Bucket, path scheme and cleanup are described by [ImageTarget], so a single
 * [upload] / [delete] pair serves every feature (avatars, inventory, pre-mades, …).
 */
interface ImageStorageRepository {
    /**
     * Uploads an image to the location described by [target].
     * @return Result containing the storage path of the uploaded image
     */
    suspend fun upload(target: ImageTarget, image: ImageData): Result<String>

    /**
     * Deletes the image(s) described by [target].
     */
    suspend fun delete(target: ImageTarget): Result<Unit>

    /**
     * Creates a signed URL for accessing an image stored at [imagePath].
     * Bucket is inferred from the path; not valid for announcement images.
     *
     * @param version identifiant du contenu attendu (l'`updated_at` de l'entité porteuse).
     * Les chemins étant déterministes, c'est le seul signal qui permet aux implémentations
     * avec cache de re-signer quand l'image a été remplacée par un autre appareil.
     */
    suspend fun getSignedUrl(imagePath: String, version: String? = null): Result<String>

    /**
     * Downloads the bytes of an image stored at [imagePath].
     * Used to copy a pre-made avatar into a player's character avatar.
     */
    suspend fun downloadImage(imagePath: String): Result<ImageData>

    /**
     * Uploads an announcement image to its dedicated (ephemeral) bucket and returns a
     * ready-to-display signed URL. Announcement images are not path-addressable like the
     * other flows because their bucket cannot be inferred from the path shape.
     */
    suspend fun uploadAnnouncementImage(tableId: String, image: ImageData): Result<String>
}
